import { useMutation, useQuery } from '@tanstack/react-query';

import { fetcher } from '@/utils/fetcher';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { Customer } from '@/types/Customer';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { useIntl } from 'react-intl';
import { getWindowLogger } from '@/utils/remoteLogger';
import { LogAction, LogStatus } from '@/types/Logging';
import { useCurrentAuthSession } from '@/context/AuthContext';
import { fsPath } from './__path';
import { useUserInfo } from './useUserInfo';

const logger = getWindowLogger({
  file: `${fsPath}/useCustomerData.ts`,
});

export const useCustomerData = (isAdmin: boolean) => {
  const { data, isLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: () =>
      fetcher<readonly Customer[]>(`${RETAIL_API_URL}/customers/readAll`),
    enabled: Boolean(isAdmin),
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
  return { data, isLoading };
};

type CreateCustomerParams = {
  name: string;
};

export const useCreateCustomer = () => {
  const { user } = useCurrentAuthSession();
  const { data: userInfo } = useUserInfo();

  const toast = useToast();
  const intl = useIntl();

  const {
    mutateAsync: createCustomer,
    isPending: isLoading,
    error,
  } = useMutation({
    mutationFn: async (params: CreateCustomerParams) => {
      const result = await fetcher<Customer>(
        `${RETAIL_API_URL}/customers/create`,
        {
          method: 'POST',
          body: JSON.stringify({ ...params }),
        },
      );

      logger.info({
        action: LogAction.CREATE,
        status: LogStatus.SUCCEEDED,
        user: user?.uid ?? 'unkown-user',
        customer: userInfo?.customer ?? 'unkown-customer',
        data: {
          created_customer: result.id,
        },
      });
      return result;
    },
    onSuccess: () => {
      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'The Customer has been created.',
          id: 'dKx+FW',
        }),
        mode: TOASTTYPE.Success,
      });
    },
  });

  return { createCustomer, isLoading, error };
};
